import React from 'react';
import { useSelector } from 'react-redux';

import { landingPageMetaProps } from '../../util/misc';
import { LayoutComposer, NamedLink } from '../../components';
import GemstoneSiteFooter from '../../components/GemstoneSiteFooter/GemstoneSiteFooter';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import StaticPage from '../PageBuilder/StaticPage';

import css from './MarketplaceLandingPage.module.css';

/* Background photography from the GemstoneX design (assets/).
   landing-cta-bg.jpg is the real design file. landing-hero-bg.png is a
   temporary emerald stand-in — the real hero file is larger than the design
   tool's 256KB fetch limit, so it must be copied in from the design assets
   (same path + name → it swaps in automatically, no code change). */
import heroBg from './images/landing-hero-bg.png';
import ctaBg from './images/landing-cta-bg.jpg';

/* ----------------------------- icons (thin line, currentColor) ----------
   Ported verbatim from the design source (ui_kits/site/landing/screens.jsx). */
const Check = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconShield = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconCert = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <circle cx="10" cy="9" r="5.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M7.5 9l1.8 1.8L12.8 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.5 13.5L5 21l5-2.4L15 21l-1.5-7.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const IconLock = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <rect x="5" y="10" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 10V7a4 4 0 1 1 8 0v3" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const Arrow = props => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M4 12h15M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PILLARS = [
  {
    Icon: IconShield,
    h: 'Verified origin',
    p: 'Provenance established at source and documented against every stone before it reaches the Vault.',
  },
  {
    Icon: IconCert,
    h: 'Lab-certified',
    p: 'Independent grading from recognised laboratories: GIA, GRS, SSEF, Gübelin, AGL, GGTL, CDTEK.',
  },
  {
    Icon: IconLock,
    h: 'Escrow settlement',
    p: 'Funds held in escrow on regulated banking rails, released on confirmed delivery only.',
  },
];

const STEPS = [
  {
    n: '01',
    h: 'Apply & verify',
    p: 'Trade buyers and suppliers apply once. Our team verifies credentials before access is granted.',
  },
  {
    n: '02',
    h: 'Browse the Vault',
    p: 'Explore certified emeralds and fine pieces, de-identified and documented to one standard.',
  },
  {
    n: '03',
    h: 'Inquire on a stone',
    p: 'Request certification and trade pricing on any piece. Sellers stay anonymous throughout.',
  },
  {
    n: '04',
    h: 'Settle securely',
    p: 'Agree terms and settle through escrow on regulated banking channels, released on delivery.',
  },
];

const MarketplaceLandingPage = () => {
  // Logged-in users (any role) get two hero buttons: primary "View Stones" into
  // the marketplace search page (/s), and secondary "Explore the Vault" into the
  // Vault page. Non-logged-in visitors keep the original apply/vault hero.
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);

  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <StaticPage {...landingPageMetaProps}>
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="LandingPage" darkMode />
              </Topbar>

              <Main as="main" className={css.main}>
                <div className={css.lp}>
                  {/* Hero (light / editorial) */}
                  <header className={css.hero} style={{ backgroundImage: `url(${heroBg})` }}>
                    <div className={css.heroScrim} />
                    <div className={`${css.wrap} ${css.heroInner}`}>
                      <div className={css.heroCopy}>
                        <div className={css.eyebrow} style={{ color: 'var(--colorPrimary)' }}>
                          The global gemstone vault
                        </div>
                        <div className={css.rule} />
                        <h1 className={css.heroTitle}>
                          Source with proof.
                          <br />
                          <em>Settle with certainty.</em>
                        </h1>
                        <p className={css.heroSub}>
                          The verified B2B marketplace where dealers and jewellers source certified
                          emeralds and coloured stones.
                        </p>
                        <div className={css.heroCta}>
                          {isAuthenticated ? (
                            <>
                              <NamedLink
                                name="SearchPage"
                                className={`${css.btn} ${css.btnPrimary}`}
                              >
                                View Stones <Arrow />
                              </NamedLink>
                              <NamedLink
                                name="TheVaultPage"
                                className={`${css.btn} ${css.btnSecondary}`}
                              >
                                Explore the Vault
                              </NamedLink>
                            </>
                          ) : (
                            <>
                              <NamedLink
                                name="TradeAccessPage"
                                className={`${css.btn} ${css.btnPrimary}`}
                              >
                                Apply for Trade Access <Arrow />
                              </NamedLink>
                              <NamedLink
                                name="TheVaultPage"
                                className={`${css.btn} ${css.btnSecondary}`}
                              >
                                Explore the Vault
                              </NamedLink>
                            </>
                          )}
                        </div>
                        <div className={css.trust}>
                          <span className={css.trustItem}>
                            <Check /> Verified origin
                          </span>
                          <span className={css.trustItem}>
                            <Check /> Lab-certified
                          </span>
                          <span className={css.trustItem}>
                            <Check /> Escrow settlement
                          </span>
                        </div>
                      </div>
                    </div>
                  </header>

                  {/* Value props */}
                  <section className={css.val}>
                    <div className={css.wrap}>
                      <div className={css.eyebrow}>Why GemstoneX</div>
                      <div className={css.rule} />
                      <h2 className={css.valH2}>
                        Trade-grade infrastructure for sourcing coloured stones.
                      </h2>
                      <div className={css.pillars}>
                        {PILLARS.map(({ Icon, h, p }) => (
                          <div className={css.pillar} key={h}>
                            <Icon />
                            <h3>{h}</h3>
                            <p>{p}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* How it works */}
                  <section className={css.how}>
                    <div className={css.wrap}>
                      <div className={css.howHead}>
                        <div className={css.eyebrow}>How it works</div>
                        <div className={`${css.rule} ${css.ruleCenter}`} />
                        <h2 className={css.howH2}>
                          From application to settlement, on one standard.
                        </h2>
                      </div>
                      <div className={css.steps}>
                        {STEPS.map(({ n, h, p }) => (
                          <div className={css.step} key={n}>
                            <div className={css.stepNum}>{n}</div>
                            <h4>{h}</h4>
                            <p>{p}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </section>

                  {/* CTA band — application/log-in prompt, only for non-logged-in visitors */}
                  {!isAuthenticated && (
                    <section
                      className={css.cta}
                      style={{
                        backgroundImage: `linear-gradient(rgba(3,18,15,.88), rgba(3,12,10,.95)), url(${ctaBg})`,
                      }}
                    >
                      <div className={css.wrap}>
                        <div className={css.eyebrow} style={{ color: 'var(--colorGoldLight)' }}>
                          Membership is by application
                        </div>
                        <div className={`${css.rule} ${css.ruleCenter}`} />
                        <h2 className={css.ctaTitle}>The trade marketplace built on proof.</h2>
                        <div style={{ marginTop: '28px' }}>
                          <NamedLink name="TradeAccessPage" className={`${css.btn} ${css.btnPrimary}`}>
                            Apply for Trade Access <Arrow />
                          </NamedLink>
                        </div>
                        <p className={css.ctaMicro}>
                          Already a member?{' '}
                          <NamedLink name="LoginPage" className={css.ctaMicroLink}>
                            Log in
                          </NamedLink>
                          .
                        </p>
                      </div>
                    </section>
                  )}
                </div>
              </Main>

              <Footer>
                <GemstoneSiteFooter />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </StaticPage>
  );
};

export default MarketplaceLandingPage;
